import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard, Building2, CreditCard, BarChart3, ToggleLeft,
  ScrollText, ChevronLeft, ChevronRight, Settings,
} from "lucide-react";

const navItems = [
  { label: "Dashboard", path: "/super-admin", icon: LayoutDashboard },
  { label: "Companies", path: "/super-admin/companies", icon: Building2 },
  { label: "Subscriptions", path: "/super-admin/subscriptions", icon: CreditCard },
  { label: "Reports", path: "/super-admin/reports", icon: BarChart3 },
  { label: "Feature Toggles", path: "/super-admin/features", icon: ToggleLeft },
  { label: "Audit Logs", path: "/super-admin/audit", icon: ScrollText },
  { label: "Settings", path: "/super-admin/settings", icon: Settings },
];

interface Props {
  collapsed: boolean;
  isMobile?: boolean;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
  onToggle: () => void;
}

export function SuperAdminSidebar({ collapsed, isMobile = false, mobileOpen = false, onCloseMobile, onToggle }: Props) {
  const location = useLocation();
  const isActive = (p: string) => location.pathname === p;

  return (
    <>
      {isMobile && mobileOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={onCloseMobile}
        />
      )}
      <aside className={cn(
        "fixed left-0 top-0 z-40 flex h-screen flex-col border-r transition-all duration-300 scrollbar-thin overflow-y-auto",
        "bg-[hsl(var(--sidebar-bg))] border-[hsl(var(--sidebar-border))]",
        isMobile
          ? cn("w-[260px] shadow-xl md:hidden", mobileOpen ? "translate-x-0" : "-translate-x-full")
          : collapsed
          ? "w-16"
          : "w-[260px]"
      )}>
      <div className="flex h-14 items-center border-b border-[hsl(var(--sidebar-border))] px-4">
        {!collapsed ? (
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-destructive text-destructive-foreground font-bold text-sm">SA</div>
            <div>
              <p className="text-sm font-semibold text-[hsl(var(--sidebar-active-fg))]">Platform Admin</p>
              <p className="text-[10px] text-[hsl(var(--sidebar-fg))]">DebtCollect SaaS</p>
            </div>
          </div>
        ) : (
          <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-lg bg-destructive text-destructive-foreground font-bold text-sm">SA</div>
        )}
      </div>

      <nav className="flex-1 px-2 py-3 space-y-0.5">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => onCloseMobile?.()}
            className={cn(
              "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              "text-[hsl(var(--sidebar-fg))] hover:bg-[hsl(var(--sidebar-hover))]",
              isActive(item.path) && "bg-[hsl(var(--sidebar-active))]/10 text-[hsl(var(--sidebar-active))]",
              collapsed && "justify-center px-2"
            )}
            title={collapsed ? item.label : undefined}
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {!collapsed && <span className="flex-1">{item.label}</span>}
          </Link>
        ))}
      </nav>

      {!isMobile && (
        <button onClick={onToggle} className="flex h-10 items-center justify-center border-t border-[hsl(var(--sidebar-border))] text-[hsl(var(--sidebar-fg))] hover:bg-[hsl(var(--sidebar-hover))] transition-colors">
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      )}
      </aside>
    </>
  );
}
